import React, { FC, useState, useCallback, useMemo } from 'react'
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch } from 'react-redux'
import Typography from '@mui/material/Typography'

import {
  IVoidCallback,
} from '@kaidam/fc-shared/src/types/utils'

import {
  IField_Filestore_Type,
  IField_Storage_Access_Type,
  IField_Storage_File,
} from '@kaidam/fc-shared/src/types/storage'

import {
  FILESTORE_TYPE_PRIVATE,
  FILESTORE_TYPE_PUBLIC,
  STORAGE_ACCESS_TYPE_PRIVATE,
  STORAGE_ACCESS_TYPE_PUBLIC,
  STORAGE_ACCESS_TYPE_SHARED,
} from '@kaidam/fc-shared/src/constants'

import {
  isImageExtension,
  isFolder,
} from '@kaidam/fc-shared/src/utils/file'

import {
  getAccessTypeScore,
  getPointerAccessType,
} from '@kaidam/fc-shared/src/utils/storage'

import Window from '../dialog/Window'
import FilestoreLayout from '../filestore/Layout'
import FilestoreSection from '../filestore/Section'

import {
  ThunkDispatcher,
} from '../../types/store'

import snackbarActions from '../../store/modules/snackbar'
import filestoreActions from '../../store/modules/filestore'

export type IFileChooserFileType = 'image' | 'folder'

const useStyles = makeStyles(theme => ({
  editorContainer: {
    height: '100%',
  },
  sectionContainer: {
    height: '100%',
    padding: theme.spacing(4),
  },
}))

const FileChooser: FC<React.PropsWithChildren<{
  publicMode?: boolean,
  sharedMode?: boolean,
  displayFilter?: {
    (filename: string): boolean,
  },
  selectFilter?: {
    (filename: string): boolean,
  },
  displayTypes?: IFileChooserFileType[],
  selectTypes?: IFileChooserFileType[],
  onChoose: {
    (file: IField_Storage_File): void,
  },
  onClose: IVoidCallback,
}>> = ({
  publicMode = false,
  sharedMode = false,
  displayFilter,
  selectFilter,
  displayTypes = [],
  selectTypes = [],
  onChoose,
  onClose,
}) => {
  
  const dispatch = useDispatch()
  const thunkDispatch = dispatch as ThunkDispatcher
  const classes = useStyles()

  const [ copyFile, setCopyFile ] = useState<{
    file: IField_Storage_File,
    type: IField_Storage_Access_Type,
  } | null>(null)
  const [ section, setSection ] = useState<IField_Filestore_Type>(publicMode ? FILESTORE_TYPE_PUBLIC : (sharedMode ? STORAGE_ACCESS_TYPE_SHARED : FILESTORE_TYPE_PRIVATE))
  const [ path, setPath ] = useState('')

  if(publicMode && sharedMode) {
    sharedMode = false
  }

  const requiredAccessType = useMemo<IField_Storage_Access_Type>(() => {
    if(publicMode) return STORAGE_ACCESS_TYPE_PUBLIC
    if(sharedMode) return STORAGE_ACCESS_TYPE_SHARED
    return STORAGE_ACCESS_TYPE_PRIVATE
  }, [
    publicMode,
    sharedMode,
  ])

  const onOpenSection = useCallback((section: IField_Filestore_Type) => {
    setSection(section)
    setPath('')
  }, [])


  const onChooseFile = useCallback(async (file: IField_Storage_File) => {
    // the access type of the chosen file
    const storageType = getPointerAccessType(file.pointer)

    if(getAccessTypeScore(storageType) >= getAccessTypeScore(requiredAccessType)) {
      onChoose(file)
      return
    }

    setCopyFile({
      file,
      type: requiredAccessType,
    })

  }, [
    requiredAccessType,
    onChoose,
  ])

  const onConfirmCopyPrivateFile = useCallback(async () => {    
    const publicFile = await thunkDispatch(filestoreActions.copyFileToOtherDrive(copyFile))
    if(!publicFile) {
      dispatch(snackbarActions.setError(`copy file failed`))
    }
    else {
      onChoose(publicFile)
    }
    setCopyFile(null)
  }, [
    copyFile,
  ])

  const useDisplayFilter = useCallback((filename: string): boolean => {
    if(displayFilter) return displayFilter(filename)
    if(displayTypes.length > 0) {
      const matchingType = displayTypes.find(type => {
        if(type == 'image') {
          return isImageExtension(filename)
        }
        else if(type == 'folder') {
          return isFolder(filename)
        }
        else {
          return false
        }
      })
      return matchingType ? true : false
    }
    else {
      return true
    }
  }, [
    displayFilter,
  ])

  const useSelectFilter = useCallback((filename: string): boolean => {
    if(selectFilter) return selectFilter(filename)
    if(selectTypes.length > 0) {
      const matchingType = selectTypes.find(type => {
        if(type == 'image') {
          return isImageExtension(filename)
        }
        else if(type == 'folder') {
          return isFolder(filename)
        }
        else {
          return false
        }
      })
      return matchingType ? true : false
    }
    else {
      return true
    }
  }, [
    selectFilter,
  ])

  return (
    <>
      <Window
        open
        size="xl"
        withCancel
        fullHeight
        noScroll
        compact
        onCancel={ onClose }
      >
        <div className={ classes.editorContainer }>
          <FilestoreLayout
            showQuickUpload
            section={ section }
            path={ path }
            onSetPath={ setPath }
            onOpenSection={ onOpenSection }
          >
            <div className={ classes.sectionContainer }>
              <FilestoreSection
                path={ path }
                section={ section }
                onSetPath={ setPath }
                onChooseFile={ onChooseFile }
                displayFilter={ useDisplayFilter }
                selectFilter={ useSelectFilter }
              />
            </div>
          </FilestoreLayout>
        </div>
      </Window>
      {
        copyFile && (
          <Window
            open
            size="sm"
            title={`Copy file to ${copyFile.type} drive?`}
            withCancel
            submitTitle={`Copy to ${copyFile.type} drive`}
            onCancel={ () => setCopyFile(null) }
            onSubmit={ onConfirmCopyPrivateFile }
          >
            <Typography>
              To add this file it must first be copied to the public drive, do you want to copy it?
            </Typography>
          </Window>
        )
      }
    </>
  )
}

export default FileChooser